import { HttpClient } from 'api/http-client';
import { LinkedResource } from 'api/tasks';
import { IntegrationField } from '../types';
import {
  HubspotConfig,
  HubspotFieldMapping,
  HubspotIntegrationProvider,
  HubspotObject,
  HubspotResourceMapping,
} from './types';

const base = '/integrations/hubspot';

class HubspotHTTPClient extends HttpClient {
  public getAuthDetails = (): Promise<HubspotIntegrationProvider> =>
    this.get<HubspotIntegrationProvider>('/integration-providers/hubspot/meta');

  public disconnectIntegration = (integrationId: number): Promise<void> =>
    this.delete<void>(`${base}/${integrationId}`);

  public getIntegrationConfig = (): Promise<HubspotConfig[]> =>
    this.get<HubspotConfig[]>(base);

  public getIntegrationConfigById = (
    integrationId: number,
  ): Promise<HubspotConfig> =>
    this.get<HubspotConfig>(`${base}/${integrationId}`);

  public getResourceMappings = (
    integrationId: number,
    nativeResourceType: string,
  ): Promise<HubspotResourceMapping[]> =>
    this.get<HubspotResourceMapping[]>(
      `${base}/${integrationId}/resource-mapping-config?nativeResourceType=${nativeResourceType}`,
    );

  public getExternalResourceTypes = (
    integrationId: number,
  ): Promise<HubspotObject[]> =>
    this.get<HubspotObject[]>(
      `${base}/${integrationId}/field-mapping-config-builder/external-resource-types`,
    );

  public getExternalResourceFields = (
    integrationId: number,
    externalResourceType: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/field-mapping-config-builder/external-resource-types/${externalResourceType}/fields`,
    );

  public getNativeResourceTypes = (
    integrationId: number,
  ): Promise<HubspotResourceMapping[]> =>
    this.get<HubspotResourceMapping[]>(
      `${base}/${integrationId}/field-mapping-config-builder/native-resource-types`,
    );

  public getMappableNativeResourceFields = (
    integrationId: number,
    nativeResourceType: string,
    externalResourceType: string,
    externalResourceFieldId: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/field-mapping-config-builder/native-resource-types/${nativeResourceType}/mappable-fields?externalResourceType=${externalResourceType}&externalFieldId=${externalResourceFieldId}`,
    );

  public createFieldMapping = (
    integrationId: number,
    resourceObjectId: number,
    payload: HubspotFieldMapping,
  ): Promise<HubspotFieldMapping> =>
    this.post(
      `${base}/${integrationId}/resource-mapping-config/${resourceObjectId}/field-mapping-config`,
      payload,
    );

  public deleteFieldMapping = (
    integrationId: number,
    resourceId: number,
    fieldMappingId: number,
  ): Promise<void> =>
    this.delete(
      `${base}/${integrationId}/resource-mapping-config/${resourceId}/field-mapping-config/${fieldMappingId}`,
    );

  public updateFieldMapping = (
    integrationId: number,
    resourceObjectId: number,
    fieldMappingId: number,
    payload: HubspotFieldMapping,
  ): Promise<HubspotFieldMapping> =>
    this.put(
      `${base}/${integrationId}/resource-mapping-config/${resourceObjectId}/field-mapping-config/${fieldMappingId}`,
      payload,
    );

  public createResourceObject = (
    integrationId: number,
    payload: Pick<
      HubspotResourceMapping,
      | 'externalIntegrationType'
      | 'externalResourceType'
      | 'nativeResourceType'
      | 'syncType'
      | 'enabled'
    >,
  ): Promise<HubspotResourceMapping> =>
    this.post(`${base}/${integrationId}/resource-mapping-config/`, payload);

  public deleteResourceObject = (
    integrationId: number,
    resourceId: number,
  ): Promise<void> =>
    this.delete(
      `${base}/${integrationId}/resource-mapping-config/${resourceId}`,
    );

  public linkHubspotObject = (
    integrationId: number,
    payload,
  ): Promise<LinkedResource> =>
    this.post(
      `${base}/${integrationId}/api/hsobject/resource-mapping`,
      payload,
    );

  public linkHubspotBudget = (
    integrationId: number,
    payload: { hsObjectUrl: string; nativeResourceId: number },
  ): Promise<LinkedResource> => {
    const v2BaseURL = this.BASEURL.replace(/\/v1$/, '/v2');

    return this.post(
      `${base}/${integrationId}/api/projectFinancialsBudget/hsobject/resource-mapping`,
      payload,
      { baseURL: v2BaseURL },
    );
  };

  public getHubspotObjectWithProject = (
    integrationId?: number,
    externalResourceId?: string,
    externalResourceType?: string,
  ): Promise<any> =>
    this.get(
      `${base}/${integrationId}/api/hsobject/${externalResourceType}/${externalResourceId}`,
    );

  public unLinkHubspotObjectWithProject = (
    integrationId?: number,
    resourceMappingId?: number,
  ): Promise<HubspotObject> =>
    this.delete(
      `${base}/${integrationId}/resource-mapping/${resourceMappingId}`,
    );
}

export const HubspotAPI = new HubspotHTTPClient();
